import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@paribus/shared-ui";
import { Download, FileText, RefreshCw, Settings } from "lucide-react";
import { useEffect, useState } from "react";

import type { MedChronExportMenuViewProps } from "./types";

const menuItemClassName =
  "flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-muted-foreground transition-colors hover:bg-surface-sunken disabled:cursor-not-allowed disabled:opacity-50";

/**
 * Cases-list presenter: one trigger opens a popover with Export PDF, Export DOCX and, when supplied, Export As.
 * - Deliberately Popover, not DropdownMenu: its pointerdown-open in a ~20px cell self-selected the first item.
 * - Trigger and content stop click propagation: React bubbles even portaled clicks to the clickable row.
 * - Closes on scroll of the cases table so it can't drift away from the trigger as the row moves.
 */
export const MedChronExportMenuDropdownView = ({
  isExportingPdf,
  isExportingDocx,
  disabled,
  onExportPdf,
  onExportDocx,
  onCustomize,
}: MedChronExportMenuViewProps) => {
  const isExportingAny = isExportingPdf || isExportingDocx;
  const isTriggerDisabled = disabled || isExportingAny;

  const triggerLabel = isExportingAny ? "Generating…" : "Download Medical Chronology";

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const close = () => setIsOpen(false);
    window.addEventListener("scroll", close, true);
    return () => window.removeEventListener("scroll", close, true);
  }, [isOpen]);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={triggerLabel}
          aria-label={triggerLabel}
          // 1.625rem square to line up with the cases-list row actions this sits among.
          className={cn(
            "group relative flex h-[1.625rem] w-[1.625rem] items-center justify-center rounded-lg transition-colors",
            isTriggerDisabled
              ? "cursor-not-allowed text-text-disabled"
              : "text-muted-foreground hover:bg-primary-subtle hover:text-primary-emphasis"
          )}
          disabled={isTriggerDisabled}
          onClick={(event) => event.stopPropagation()}
        >
          {isExportingAny ? (
            <RefreshCw className="h-5 w-5 animate-spin" strokeWidth={1.5} aria-hidden />
          ) : (
            <Download className="h-5 w-5" strokeWidth={1.5} aria-hidden />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        aria-label="Export format options"
        align="start"
        sideOffset={6}
        className="flex w-auto min-w-[10rem] flex-col border-0 bg-card p-0 py-1 shadow-lg ring-1 ring-black/5"
        onClick={(event) => event.stopPropagation()}
      >
        <PopoverClose asChild>
          <button
            type="button"
            disabled={disabled}
            className={menuItemClassName}
            onClick={onExportPdf}
          >
            <FileText className="h-4 w-4 text-primary" aria-hidden />
            Export PDF
          </button>
        </PopoverClose>
        <PopoverClose asChild>
          <button
            type="button"
            disabled={disabled}
            className={menuItemClassName}
            onClick={onExportDocx}
          >
            <Download className="h-4 w-4 text-chart-3" aria-hidden />
            Export DOCX
          </button>
        </PopoverClose>
        {onCustomize && (
          <PopoverClose asChild>
            <button
              type="button"
              disabled={disabled}
              className={menuItemClassName}
              onClick={onCustomize}
            >
              <Settings className="h-4 w-4" aria-hidden />
              Export As
            </button>
          </PopoverClose>
        )}
      </PopoverContent>
    </Popover>
  );
};
